import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'LuxKit/Button/TextButton'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import moment from 'moment'
import React, { useCallback, useContext, useMemo } from 'react'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import AccommodationPackageTileHeaderArea from 'components/AccommodationKit/AccommodationPackageTileHeaderArea'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import OfferPageLoyaltyStayUpgradeCallOutBanner from '../Loyalty/OfferPageLoyaltyStayUpgradeCallOutBanner'
import FlashHotelOptionPriceDetails from './FlashHotelOptionPriceDetails'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import useLoyaltyOfferPageTargetStayUpgrade from '../hooks/useLoyaltyOfferPageTargetStayUpgrade'
import useLoyaltyOfferPageClearTargetStayUpgrade from '../hooks/useLoyaltyOfferPageClearTargetStayUpgrade'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Expander from 'components/utils/Expander'
import useLoyaltyOfferPageSelectedTargetUpgradeOrigin from '../hooks/useLoyaltyOfferPageSelectedTargetUpgradeOrigin'
import useLoyaltyTargetUpgradePackages from 'loyalty/hooks/useLoyaltyTargetUpgradePackages'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import { useIntl } from 'react-intl'

const InclusionsContainer = styled.div`
  display: grid;
  grid-template-areas: 'base-package' 'target-upgrade-package';
  grid-template-rows: 1fr;
`

const BasePackageInclusions = styled.div`
  grid-area: base-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;
  
  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const TargetUpgradePackageInclusions = styled.div`
  grid-area: target-upgrade-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(${rem(0)});
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;
  
  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

interface Props {
  optionNumber: number
  offer: App.Offer
  roomsBeforeSurcharge?: number
  packageView: App.OfferPackageOptionView
  checkIn?: string
  duration: number
  previewMode: boolean
  isLoyaltyEnabled: boolean
  showOptionLabel?: boolean
  availableForPurchase?: boolean
  suggestedPackageMessage?: string
}

function FlashHotelDesktopRateOption(props: Props) {
  const {
    offer,
    packageView,
    roomsBeforeSurcharge,
    checkIn,
    duration,
    optionNumber,
    previewMode,
    isLoyaltyEnabled,
    showOptionLabel = true,
    availableForPurchase,
    suggestedPackageMessage,
  } = props

  const intl = useIntl()
  const { partnership } = useContext(GeoContext)
  const {
    package: pkg,
  } = packageView

  const showPartnership =
    (packageView.hotelAvailable &&
      !!partnership?.hasEarn &&
      offer.canEarnPartnershipPoints)

  const showModal = useContext(ModalContext)
  const showPrices = (availableForPurchase && packageView.capacityValid && packageView.hotelAvailable) || previewMode
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    selectedTargetUpgradePackage,
    selectedTargetUpgradeType,
  } = useLoyaltyOfferPageTargetStayUpgrade(pkg)

  const selectedTargetUpgradeOrigin = useLoyaltyOfferPageSelectedTargetUpgradeOrigin(pkg)
  const targetUpgradePackages = useLoyaltyTargetUpgradePackages(pkg, offer)
  const loyaltyRoomUpgradePkg = targetUpgradePackages?.room
  const loyaltyPackageUpgradePkg = targetUpgradePackages?.package

  const {
    inclusions: loyaltyRoomUpgradeInclusions,
    bonusInclusions: loyaltyRoomUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyRoomUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    inclusions: loyaltyPackageUpgradeInclusions,
    bonusInclusions: loyaltyPackageUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyPackageUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const activeInclusionCategories = useActiveInclusionFilters()

  // Boost regular package inclusions based on active filters (not bonus or loyalty inclusions)
  const boostedInclusions = useMemo(() => {
    if (!inclusions.length) return inclusions
    return reorderTileInclusionsByActiveFilters(inclusions, activeInclusionCategories)
  }, [inclusions, activeInclusionCategories])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const loyaltyRoomUpgradeBenefitDetails = useLoyaltyUserBenefitView('hotel_upgrade')

  useLoyaltyOfferPageClearTargetStayUpgrade(packageView)
  return (
    <Expander expanded={!selectedTargetUpgradeOrigin.package} contentTransition="delayed">
      {suggestedPackageMessage && <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{suggestedPackageMessage}</MessageBanner>}
      <AccommodationPackageTile data-testid={pkg.uniqueKey}>
        {(isLoyaltyEnabled && !!loyaltyRoomUpgradeBenefitDetails?.availability) && <AccommodationPackageTileHeaderArea>
          <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} />
        </AccommodationPackageTileHeaderArea>}
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            packageNumber={showOptionLabel ? optionNumber : undefined}
            roomPackage={packageView.package}
            selectedTargetUpgradePackage={selectedTargetUpgradePackage}
            targetUpgradePackage={targetUpgradePackages?.room ?? targetUpgradePackages?.package}
          >
            <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              {availableForPurchase && offer.property && <>
                {!selectedTargetUpgradePackage && pkg.roomRate && <FlashCancellationPolicies
                  checkInDate={checkIn}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                />}
                {selectedTargetUpgradePackage?.roomRate && <FlashCancellationPolicies
                  checkInDate={checkIn}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
                />}
              </>}
              {!!showPartnership && pkg.partnership && <OfferPackagePartnershipLabel
                offer={offer}
                price={packageView.hotelPrice}
                packagePartnership={pkg.partnership}
              />}
            </Group>
          </AccommodationPackageTileDetails>
          <InclusionsContainer>
            <BasePackageInclusions className={clsx({ 'show': !selectedTargetUpgradePackage })}>
              <LargeContentToggle
                maxHeight={208}
                showMoreText={intl.formatMessage({
                  id: 'H4ATJCPhDL',
                  defaultMessage: 'View more inclusions',
                  description: 'Text to show more inclusions',
                })}
                showLessText={intl.formatMessage({
                  id: 'v3fu9Hcksv',
                  defaultMessage: 'View less inclusions',
                  description: 'Text to show less inclusions',
                })}
              >
                <VerticalSpacer gap={8}>
                  {!!luxPlusInclusions.length && <LuxPlusInclusions
                    luxPlusInclusions={luxPlusInclusions}
                    accommodationOffer={offer}
                    hideUpsellModal
                  />}
                  <InclusionsSection inclusions={boostedInclusions} bonusInclusions={bonusInclusions} />
                </VerticalSpacer>
              </LargeContentToggle>
            </BasePackageInclusions>
            {loyaltyRoomUpgradePkg && <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'room' })}>
              <LargeContentToggle
                maxHeight={208}
                showMoreText={intl.formatMessage({
                  id: 'H4ATJCPhDL',
                  defaultMessage: 'View more inclusions',
                  description: 'Text to show more inclusions',
                })}
                showLessText={intl.formatMessage({
                  id: 'v3fu9Hcksv',
                  defaultMessage: 'View less inclusions',
                  description: 'Text to show less inclusions',
                })}
              >
                <VerticalSpacer gap={8}>
                  {!!loyaltyRoomUpgradeLuxPlusInclusions.length && <LuxPlusInclusions
                    luxPlusInclusions={loyaltyRoomUpgradeLuxPlusInclusions}
                    accommodationOffer={offer}
                    hideUpsellModal
                  />}
                  <InclusionsSection inclusions={loyaltyRoomUpgradeInclusions} bonusInclusions={loyaltyRoomUpgradeBonusInclusions} />
                </VerticalSpacer>
              </LargeContentToggle>
            </TargetUpgradePackageInclusions>}
            {loyaltyPackageUpgradePkg && <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'package' })}>
              <LargeContentToggle
                maxHeight={208}
                showMoreText={intl.formatMessage({
                  id: 'H4ATJCPhDL',
                  defaultMessage: 'View more inclusions',
                  description: 'Text to show more inclusions',
                })}
                showLessText={intl.formatMessage({
                  id: 'v3fu9Hcksv',
                  defaultMessage: 'View less inclusions',
                  description: 'Text to show less inclusions',
                })}
              >
                <VerticalSpacer gap={8}>
                  {!!loyaltyPackageUpgradeLuxPlusInclusions.length && <LuxPlusInclusions
                    luxPlusInclusions={loyaltyPackageUpgradeLuxPlusInclusions}
                    accommodationOffer={offer}
                    hideUpsellModal
                  />}
                  <InclusionsSection inclusions={loyaltyPackageUpgradeInclusions} bonusInclusions={loyaltyPackageUpgradeBonusInclusions} />
                </VerticalSpacer>
              </LargeContentToggle>
            </TargetUpgradePackageInclusions>}
          </InclusionsContainer>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          <WalledContent
            enforced={offer.walledGarden}
            wall={(
              <Group fullHeight direction="vertical" verticalAlign="end">
                <TextButton kind="secondary" onClick={onSignUpClick}>
                  {intl.formatMessage({
                    id: 'gygh0xqT03',
                    defaultMessage: 'Sign up to view exclusive rates',
                    description: 'Sign up button text to view exclusive rates',
                  })}
                </TextButton>
              </Group>
            )}
          >
            {showPrices && <FlashHotelOptionPriceDetails
              roomsBeforeSurcharge={roomsBeforeSurcharge}
              packageView={packageView}
            />}
          </WalledContent>
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
    </Expander>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  previewMode: appState.system.previewMode,
  isLoyaltyEnabled: getIsLoyaltyEnabled(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), FlashHotelDesktopRateOption, preventRenderWithOpenFilter)
